import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Shield, Loader2 } from 'lucide-react'

interface PortalUser {
  id: number
  username: string
  email: string | null
  full_name: string | null
  role: string
  status: string
  client_id: string
  tenant_id: string | null
  created_at: string
  last_login_at: string | null
}

export function PortalUsersPage() {
  const [users, setUsers] = useState<PortalUser[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('')

  useEffect(() => {
    fetch('/api/portal/users')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.users) setUsers(data.users)
      })
      .finally(() => setLoading(false))
  }, [])

  const filtered = filter
    ? users.filter(u =>
        u.client_id === filter ||
        u.tenant_id === filter
      )
    : users

  const tenantIds = [...new Set(users.map(u => u.client_id).filter(Boolean))]

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-brand-500" />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/portal" className="text-ink-soft hover:text-ink-dark transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-ink-dark">All Users</h1>
          <p className="text-sm text-ink-soft">{users.length} users across all copilots</p>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setFilter('')}
          className={`text-xs font-medium px-3 py-1 rounded-full transition-colors ${
            !filter ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          All ({users.length})
        </button>
        {tenantIds.map(tid => {
          const count = users.filter(u => u.client_id === tid).length
          return (
            <button
              key={tid}
              type="button"
              onClick={() => setFilter(tid)}
              className={`text-xs font-medium px-3 py-1 rounded-full transition-colors ${
                filter === tid ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {tid} ({count})
            </button>
          )
        })}
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left text-xs text-ink-soft uppercase tracking-wider">
              <th className="px-4 py-2.5">User</th>
              <th className="px-4 py-2.5">Copilot</th>
              <th className="px-4 py-2.5">Role</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Joined</th>
              <th className="px-4 py-2.5">Last Login</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-25">
                <td className="px-4 py-2.5">
                  <div className="font-medium text-ink-dark">{u.full_name || u.username}</div>
                  {u.email && <div className="text-xs text-ink-soft">{u.email}</div>}
                </td>
                <td className="px-4 py-2.5">
                  <Link
                    to={`/portal/tenants/${u.client_id}`}
                    className="text-xs font-medium text-brand-600 hover:underline"
                  >
                    {u.client_id}
                  </Link>
                </td>
                <td className="px-4 py-2.5">
                  <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                    u.role === 'admin'
                      ? 'bg-amber-50 text-amber-700'
                      : 'bg-slate-50 text-slate-600'
                  }`}>
                    {u.role === 'admin' && <Shield className="w-3 h-3" />}
                    {u.role}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <span className={`text-xs font-medium ${
                    u.status === 'active' ? 'text-green-600' : 'text-slate-400'
                  }`}>
                    {u.status || 'active'}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-xs text-ink-soft">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                </td>
                <td className="px-4 py-2.5 text-xs text-ink-soft">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : 'Never'}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-ink-soft text-sm">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
